import { Button, Group, Paper, Text, Title } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HarvestProofTable } from '../../components/harvest-proof-table/harvest-proof-table';

import { useAuth } from '@auth/data-access';
import { FORM_STATUS, hasAdminAccess, useSharedQueries } from '@data-access';
import { harvestProofApi } from '@harvest-proof/data-access';
import { FullMainHeight } from '@ui';

export function HarvestProofListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = hasAdminAccess(user?.user_access);

  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);

  const prepSynthesis = useMutation({
    mutationFn: harvestProofApi.prepSynthesis,
    onSuccess: (data) => {
      setComputationRequestId(data.instance_id);
    },
  });

  const downloadSynthesis = useMutation({
    mutationFn: harvestProofApi.downloadSynthesis,
  });

  const sharedQueries = useSharedQueries();
  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  const downloadedRef = useRef<number | null>(null);

  useEffect(() => {
    const data = computationRequest.data;
    if (
      data?.status === FORM_STATUS.FINISHED &&
      downloadedRef.current !== data.id
    ) {
      downloadedRef.current = data.id;
      downloadSynthesis.mutate({ id: data.id });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadSynthesis]);

  const isLoading =
    prepSynthesis.isPending ||
    computationRequest.data?.status === FORM_STATUS.PENDING;

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper p="md" shadow="xs">
          <Group justify="space-between">
            <div>
              <Title order={2}>{t('harvest-proof.title')}</Title>
              <Text c="dimmed" mt="xs" size="sm">
                {t('harvest-proof.list.description')}
              </Text>
            </div>
            {canManage && (
              <Button
                leftSection={<IconDownload size={16} />}
                loading={isLoading}
                onClick={() => prepSynthesis.mutate()}
                variant="light"
              >
                {t('harvest-proof.actions.downloadSynthesis')}
              </Button>
            )}
          </Group>
        </Paper>
      </FullMainHeight.Header>

      <FullMainHeight.Content>
        <HarvestProofTable />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}
